import { apolloClient } from "@/apollo/index";

import resizeMasterTemplateMutation from "@/apollo/mutations/ResizeMasterTemplate.gql";
import campaignEditorQuery from "@/apollo/queries/v2/CampaignEditor.gql";

import type { MasterTemplate } from "shared-types";

const INDESIGN_RESIZE_POLL_INTERVAL_MS = 2000;

type ResizeMasterTemplatePayload = {
    masterTemplate: MasterTemplate | null;
    resource: {
        resourceId: string;
        status: string;
        percentage: number;
    } | null;
};

function sleep(durationMs: number) {
    return new Promise(resolve => {
        window.setTimeout(resolve, durationMs);
    });
}

export async function resizeMasterTemplate({
    width,
    height,
    masterTemplate,
    masterTemplateId,
    campaignId,
    sandboxMode
}: {
    width: number;
    height: number;
    clientId: string;
    masterTemplate: MasterTemplate;
    masterTemplateId: string;
    campaignId: string;
    sandboxMode: boolean;
}) {
    if (!apolloClient) {
        throw new Error("Apollo client is not initialized");
    }

    const client = apolloClient;

    const refetchCampaign = async () => {
        await client.query({
            query: campaignEditorQuery,
            variables: {
                campaignId
            },
            context: {
                useLocalDevLink: sandboxMode
            },
            fetchPolicy: "network-only"
        });
    };

    const pollResizeMasterTemplate = async (initiate = false): Promise<MasterTemplate> => {
        const { data, errors } = await client.mutate<{
            resizeMasterTemplate: ResizeMasterTemplatePayload;
        }>({
            mutation: resizeMasterTemplateMutation,
            variables: {
                width,
                height,
                masterTemplateId,
                campaignId,
                initiate
            },
            context: {
                useLocalDevLink: sandboxMode
            }
        });

        if (errors?.length) {
            throw new Error(errors.map(e => e.message).join(", "));
        }

        if (data?.resizeMasterTemplate.masterTemplate) {
            await refetchCampaign();
            return data.resizeMasterTemplate.masterTemplate;
        }

        if (masterTemplate.adType !== "INDESIGN" || !data?.resizeMasterTemplate.resource) {
            throw new Error(`Failed to resize master template. Please try again.`);
        }

        await sleep(INDESIGN_RESIZE_POLL_INTERVAL_MS);
        return pollResizeMasterTemplate();
    };

    return pollResizeMasterTemplate(true);
}
